import { usePage } from '@inertiajs/react';
import { Github } from '@/components/icons';
import { Button } from '@/components/ui/button';
import type { StateResponse } from '@/types/market';
import { formatBananas } from './format';
import { githubSignInPath } from './github';
import { useLivePortfolio, useMarketState } from './live';
import { RollingNumber } from './rolling-number';

export function MarketHeader({
  initialData,
  hackSlug,
  eventLabel,
}: {
  initialData: StateResponse;
  hackSlug: string;
  eventLabel: string;
}) {
  const pathname = usePage().url.split('?')[0];
  const state = useMarketState(initialData);

  // Cash plus positions at the live price — the number the leaderboard ranks on, without
  // fetching the whole board for one row.
  const isHacker = state.canTrade ?? state.myUserType === 'hacker';
  const { portfolio } = useLivePortfolio(state, isHacker);
  const account = portfolio && !('error' in portfolio) ? portfolio : null;
  const totalBananas = account?.totalValueBananas ?? null;
  const cashBananas = account?.cashBananas ?? state.myBalance;

  const statusLabel = {
    setup: 'En preparación',
    open: 'Abierto',
    closed: 'Cerrado',
    resolved: 'Resuelto',
  }[state.market.status];
  const isOpen = state.market.status === 'open';

  return (
    <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
      <div className="flex flex-col gap-4 border-primary/20 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 font-mono text-primary/70 text-xs uppercase tracking-wider">
            {eventLabel}
          </p>
          <h1 className="font-bold text-3xl text-primary sm:text-4xl">
            🍌 Platanus Hack Market
          </h1>
        </div>
        <div
          className={`inline-flex items-center gap-2 px-3 py-2 font-mono text-xs uppercase tracking-wider ${
            isOpen
              ? 'bg-primary text-background'
              : 'border-2 border-primary text-primary'
          }`}
        >
          <span
            className={`size-2 ${isOpen ? 'bg-background' : 'bg-primary'}`}
          />
          {statusLabel}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        {isHacker && cashBananas !== null ? (
          <div className="border-primary border-l-4 pl-4">
            <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
              Tu total
            </p>
            <p className="font-bold font-mono text-2xl text-primary tabular-nums">
              <RollingNumber
                value={totalBananas ?? cashBananas}
                format={formatBananas}
              />
            </p>
            <p className="font-mono text-[10px] text-primary/60 uppercase tracking-wider">
              Cash <RollingNumber value={cashBananas} format={formatBananas} />
              {totalBananas !== null && totalBananas !== cashBananas && (
                <>
                  {' · '}
                  Posiciones{' '}
                  <RollingNumber
                    value={totalBananas - cashBananas}
                    format={formatBananas}
                  />
                </>
              )}
            </p>
          </div>
        ) : state.myUserType === null ? (
          <Button
            onClick={() => {
              window.location.href = githubSignInPath(pathname);
            }}
            className="rounded-none bg-primary font-bold text-background uppercase tracking-wider hover:bg-primary/90"
          >
            <Github className="mr-2 size-4" />
            Iniciar sesión
          </Button>
        ) : (
          <div />
        )}
        <nav className="flex flex-wrap gap-2 font-mono text-xs uppercase tracking-wider">
          <a
            href={`/${hackSlug}/market`}
            className="bg-primary px-3 py-2 text-background"
          >
            Mercado
          </a>
          {isHacker && (
            <a
              href={`/${hackSlug}/market/portfolio`}
              className="border-2 border-primary px-3 py-2 text-primary transition-colors hover:bg-primary hover:text-background"
            >
              Portafolio
            </a>
          )}
          <a
            href={`/${hackSlug}/market/leaderboard`}
            className="border-2 border-primary px-3 py-2 text-primary transition-colors hover:bg-primary hover:text-background"
          >
            Leaderboard
          </a>
        </nav>
      </div>
    </div>
  );
}
